import React from 'react'
import { Box, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import type { FkspProcessedFilter, FkspStatusFilter, FkspTypeFilter, FkspFilledFilter, FkspEkonomSortField } from '../lib/fkspConstants'
import { FKSP_BENEFIT_OPTIONS, FKSP_PRIPOJISTENI_OPTIONS } from '../lib/fkspConstants'
import { FKSP_STATUS_FILTER_OPTIONS } from '../lib/fkspStatusLabels'

export type FkspRequestsToolbarProps = {
  search: string
  onSearchChange: (value: string) => void
  createdFrom: string
  onCreatedFromChange: (value: string) => void
  createdTo: string
  onCreatedToChange: (value: string) => void
  sortField: FkspEkonomSortField
  onSortFieldChange: (value: FkspEkonomSortField) => void
  dateNewestFirst: boolean
  onDateSortChange: (newestFirst: boolean) => void
  disabled?: boolean
  /** Rozšířené filtry (ekonom apod.) */
  extendedFilters?: boolean
  typeFilter?: FkspTypeFilter
  onTypeFilterChange?: (value: FkspTypeFilter) => void
  processedFilter?: FkspProcessedFilter
  onProcessedFilterChange?: (value: FkspProcessedFilter) => void
  statusFilter?: FkspStatusFilter
  onStatusFilterChange?: (value: FkspStatusFilter) => void
  benefitSubtypeFilter?: string
  onBenefitSubtypeFilterChange?: (value: string) => void
  /** Filtry a řazení pro hlavního ekonoma */
  ekonomFilters?: boolean
  doplacenoFilter?: FkspFilledFilter
  onDoplacenoFilterChange?: (value: FkspFilledFilter) => void
  datumFilter?: FkspFilledFilter
  onDatumFilterChange?: (value: FkspFilledFilter) => void
}

/**
 * Společná lišta: hledání podle jména, filtr data podání, řazení.
 */
export const FkspRequestsToolbar: React.FC<FkspRequestsToolbarProps> = ({
  search,
  onSearchChange,
  createdFrom,
  onCreatedFromChange,
  createdTo,
  onCreatedToChange,
  sortField,
  onSortFieldChange,
  dateNewestFirst,
  onDateSortChange,
  disabled = false,
  extendedFilters = false,
  typeFilter = 'all',
  onTypeFilterChange,
  processedFilter = 'all',
  onProcessedFilterChange,
  statusFilter = 'all',
  onStatusFilterChange,
  benefitSubtypeFilter = '',
  onBenefitSubtypeFilterChange,
  ekonomFilters = false,
  doplacenoFilter = 'all',
  onDoplacenoFilterChange,
  datumFilter = 'all',
  onDatumFilterChange,
}) => {
  const subtypeOptions =
    typeFilter === 'pripojisteni'
      ? FKSP_PRIPOJISTENI_OPTIONS
      : typeFilter === 'benefity'
        ? FKSP_BENEFIT_OPTIONS
        : []

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        mb: 2,
        alignItems: 'center',
      }}
    >
      <TextField
        size="small"
        label="Hledat podle příjmení nebo jména"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        disabled={disabled}
        sx={{ minWidth: 240, flex: '1 1 220px' }}
      />
      <TextField
        size="small"
        type="date"
        label="Podáno od"
        value={createdFrom}
        onChange={(e) => onCreatedFromChange(e.target.value)}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 150 }}
      />
      <TextField
        size="small"
        type="date"
        label="Podáno do"
        value={createdTo}
        onChange={(e) => onCreatedToChange(e.target.value)}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 150 }}
      />
      {extendedFilters && onTypeFilterChange && (
        <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
          <InputLabel id="fksp-filter-type-label">Typ žádosti</InputLabel>
          <Select
            labelId="fksp-filter-type-label"
            label="Typ žádosti"
            value={typeFilter}
            onChange={(e) => onTypeFilterChange(e.target.value as FkspTypeFilter)}
          >
            <MenuItem value="all">Všechny typy</MenuItem>
            <MenuItem value="benefity">Benefity</MenuItem>
            <MenuItem value="pripojisteni">Připojištění</MenuItem>
          </Select>
        </FormControl>
      )}
      {extendedFilters && onProcessedFilterChange && (
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
          <InputLabel id="fksp-filter-processed-label">Zpracování ekonem</InputLabel>
          <Select
            labelId="fksp-filter-processed-label"
            label="Zpracování ekonem"
            value={processedFilter}
            onChange={(e) => onProcessedFilterChange(e.target.value as FkspProcessedFilter)}
          >
            <MenuItem value="all">Vše</MenuItem>
            <MenuItem value="unprocessed">Nezpracované</MenuItem>
            <MenuItem value="processed">Zpracované</MenuItem>
          </Select>
        </FormControl>
      )}
      {extendedFilters && onStatusFilterChange && (
        <FormControl size="small" sx={{ minWidth: 240 }} disabled={disabled}>
          <InputLabel id="fksp-filter-status-label">Stav žádosti</InputLabel>
          <Select
            labelId="fksp-filter-status-label"
            label="Stav žádosti"
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as FkspStatusFilter)}
          >
            <MenuItem value="all">Všechny stavy</MenuItem>
            {FKSP_STATUS_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {extendedFilters && onBenefitSubtypeFilterChange && typeFilter !== 'all' && (
        <FormControl size="small" sx={{ minWidth: 280, flex: '1 1 260px' }} disabled={disabled}>
          <InputLabel id="fksp-filter-subtype-label">Podtyp / kategorie</InputLabel>
          <Select
            labelId="fksp-filter-subtype-label"
            label="Podtyp / kategorie"
            value={benefitSubtypeFilter}
            onChange={(e) => onBenefitSubtypeFilterChange(e.target.value)}
          >
            <MenuItem value="">Vše v rámci typu</MenuItem>
            {subtypeOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt.length > 72 ? `${opt.slice(0, 69)}…` : opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {ekonomFilters && onDoplacenoFilterChange && (
        <FormControl size="small" sx={{ minWidth: 170 }} disabled={disabled}>
          <InputLabel id="fksp-filter-doplaceno-label">Doplaceno</InputLabel>
          <Select
            labelId="fksp-filter-doplaceno-label"
            label="Doplaceno"
            value={doplacenoFilter}
            onChange={(e) => onDoplacenoFilterChange(e.target.value as FkspFilledFilter)}
          >
            <MenuItem value="all">Vše</MenuItem>
            <MenuItem value="filled">Vyplněno</MenuItem>
            <MenuItem value="empty">Prázdné</MenuItem>
          </Select>
        </FormControl>
      )}
      {ekonomFilters && onDatumFilterChange && (
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
          <InputLabel id="fksp-filter-datum-label">Datum zaplacení</InputLabel>
          <Select
            labelId="fksp-filter-datum-label"
            label="Datum zaplacení"
            value={datumFilter}
            onChange={(e) => onDatumFilterChange(e.target.value as FkspFilledFilter)}
          >
            <MenuItem value="all">Vše</MenuItem>
            <MenuItem value="filled">Vyplněno</MenuItem>
            <MenuItem value="empty">Prázdné</MenuItem>
          </Select>
        </FormControl>
      )}
      <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
        <InputLabel id="fksp-sort-field-label">Řadit podle</InputLabel>
        <Select
          labelId="fksp-sort-field-label"
          label="Řadit podle"
          value={sortField}
          onChange={(e) => onSortFieldChange(e.target.value as FkspEkonomSortField)}
        >
          <MenuItem value="requester_name">Jméno žadatele</MenuItem>
          <MenuItem value="created_at">Datum podání</MenuItem>
          {ekonomFilters && <MenuItem value="doplaceno">Doplaceno</MenuItem>}
          {ekonomFilters && <MenuItem value="datum">Datum zaplacení</MenuItem>}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 160 }} disabled={disabled}>
        <InputLabel id="fksp-sort-order-label">Pořadí</InputLabel>
        <Select
          labelId="fksp-sort-order-label"
          label="Pořadí"
          value={dateNewestFirst ? 'desc' : 'asc'}
          onChange={(e) => onDateSortChange(e.target.value === 'desc')}
        >
          <MenuItem value="desc">Sestupně</MenuItem>
          <MenuItem value="asc">Vzestupně</MenuItem>
        </Select>
      </FormControl>
    </Box>
  )
}
